import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import LogoController from "./LogoController";
import { safeInit } from "../../../../utilities";

// Device-specific multipliers and position offsets
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.375,
      xOffsetMultiplier: -3.5,
      yOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.44,
      xOffsetMultiplier: -3.6,
      yOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.47,
      xOffsetMultiplier: -3.65,
      yOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    deviceMultiplier: 0.47,
    xOffsetMultiplier: -3.65,
    yOffsetMultiplier: 1.0,
  },
};

const LogoLandscape = ({ renderMode = "static" }) => {
  const {
    device,
    orientation,
    symbolScale,
    symbolWidth,
    symbolHeight,
    isMobileSmall,
  } = useDimensions();
  const logoControllerRef = useRef(null);
  const containerRef = useRef(null);

  const texture = ASSET_NAME.logo ? PIXI.Assets.get(ASSET_NAME.logo) : null;

  // Get device configuration - use real device for MOBILE_SMALL support
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    if (realDevice === PixiSettings.DEVICE.DESKTOP) {
      return DEVICE_CONFIG[realDevice];
    }
    return (
      DEVICE_CONFIG[realDevice]?.[orientation] || {
        deviceMultiplier: 1.0,
        xOffsetMultiplier: -4.0,
        yOffsetMultiplier: 1.35,
      }
    );
  }, [device, orientation, isMobileSmall]);

  // Get scaled dimensions - memoized (spine mode: use texture or fallback; static: requires texture)
  const scaledDimensions = useMemo(() => {
    if (texture) {
      const nativeWidth = texture.width;
      const nativeHeight = texture.height;
      const scaledWidth =
        nativeWidth * deviceConfig.deviceMultiplier * symbolScale;
      const scaledHeight =
        nativeHeight * deviceConfig.deviceMultiplier * symbolScale;
      return { scaledWidth, scaledHeight };
    }
    // Spine mode fallback when texture not yet loaded - use symbol-based size
    if (renderMode === "spine") {
      const fallbackWidth = symbolWidth * 2.5;
      const fallbackHeight = symbolHeight * 0.8;
      return {
        scaledWidth:
          fallbackWidth * deviceConfig.deviceMultiplier * symbolScale,
        scaledHeight:
          fallbackHeight * deviceConfig.deviceMultiplier * symbolScale,
      };
    }
    return { scaledWidth: 0, scaledHeight: 0 };
  }, [
    texture,
    deviceConfig.deviceMultiplier,
    symbolScale,
    renderMode,
    symbolWidth,
    symbolHeight,
  ]);

  // Create controller when showing, cleanup when hiding (like IntroPopup with introPopupActive)
  useEffect(() => {
    const shouldShow =
      renderMode === "spine" &&
      orientation !== PixiSettings.ORIENTATION.PORTRAIT;

    if (!shouldShow) {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
      return;
    }

    if (containerRef.current && !logoControllerRef.current) {
      const { scaledWidth, scaledHeight } = scaledDimensions;

      safeInit(() => {
        logoControllerRef.current = new LogoController(
          containerRef.current,
          scaledWidth,
          scaledHeight,
          deviceConfig,
        );
        logoControllerRef.current.setVisible(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, orientation]);

  // Update dimensions on resize (only when controller exists and is initialized)
  useEffect(() => {
    const controller = logoControllerRef.current;
    if (!controller?.isInitialized) return;

    const { scaledWidth, scaledHeight } = scaledDimensions;
    controller.updateDimensions(scaledWidth, scaledHeight, deviceConfig);
    controller.setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode, scaledDimensions, deviceConfig, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (logoControllerRef.current) {
        logoControllerRef.current.cleanup();
        logoControllerRef.current = null;
      }
    };
  }, []);

  // Get logo position - memoized to prevent recalculation
  const logoPosition = useMemo(() => {
    const x = symbolWidth * deviceConfig.xOffsetMultiplier; // Relative to GameContainer center
    const y = -(symbolHeight * deviceConfig.yOffsetMultiplier);
    return { x, y };
  }, [
    symbolWidth,
    symbolHeight,
    deviceConfig.xOffsetMultiplier,
    deviceConfig.yOffsetMultiplier,
  ]);

  // Don't render in portrait - landscape logo only for landscape/desktop
  if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
    return null;
  }
  if (renderMode === "static" && !texture) {
    return null;
  }

  const { scaledWidth, scaledHeight } = scaledDimensions;

  return (
    <Container
      ref={containerRef}
      x={logoPosition.x}
      y={logoPosition.y}
      zIndex={5}
      name="logo_landscape"
      sortableChildren={true}
    >
      {renderMode === "static" && texture && (
        <Sprite
          texture={texture}
          width={scaledWidth}
          height={scaledHeight}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default LogoLandscape;
